import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { PropertyListResponse } from "@screener/contracts";
import { RISK_LABEL_KO, formatWonKo, toWon } from "@screener/domain";
import { api, ApiFailure } from "../lib/api";
import SampleBanner from "../components/SampleBanner";

const TYPE_LABEL: Record<string, string> = {
  apartment: "아파트",
  officetel: "오피스텔",
  villa: "빌라",
  house: "단독",
  commercial: "상가",
  land: "토지",
  other: "기타",
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: "매각기일 지정",
  postponed: "기일 변경",
  withdrawn: "취하",
  canceled: "취소",
  failed: "유찰",
  sold: "매각",
  unknown: "확인 안 됨",
};

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<PropertyListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .properties(params.toString())
      .then((d) => alive && setData(d))
      .catch((e: unknown) =>
        alive
          ? setError(e instanceof ApiFailure ? e.message : "목록을 불러오지 못했다")
          : undefined,
      );
    return () => {
      alive = false;
    };
  }, [params]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setParams(next);
  }

  return (
    <div>
      <SampleBanner />
      <h1 className="text-[26px] font-extrabold tracking-tight">물건 목록</h1>

      <div className="mt-5 flex flex-wrap gap-2">
        <select
          className="rounded-full border border-line bg-canvas px-4 py-2 text-[13px]"
          value={params.get("propertyType") ?? ""}
          onChange={(e) => setParam("propertyType", e.target.value)}
        >
          <option value="">유형 전체</option>
          <option value="apartment">아파트</option>
          <option value="officetel">오피스텔</option>
        </select>
        <select
          className="rounded-full border border-line bg-canvas px-4 py-2 text-[13px]"
          value={params.get("status") ?? ""}
          onChange={(e) => setParam("status", e.target.value)}
        >
          <option value="">상태 전체</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-[13px]">
          <input
            type="checkbox"
            checked={params.get("excludeHardFiltered") === "true"}
            onChange={(e) =>
              setParam("excludeHardFiltered", e.target.checked ? "true" : "")
            }
          />
          하드필터 탈락 숨기기
        </label>
      </div>

      {error && (
        <p className="mt-6 rounded-2xl border border-stop/30 bg-stop-soft px-4 py-3 text-[13px] text-stop">
          {error}
        </p>
      )}

      {data && (
        <>
          <p className="mt-6 text-[13px] text-ink-soft">
            전체 {data.meta.total}건 · {data.meta.page} / {data.meta.totalPages} 페이지
          </p>
          <ul className="mt-3 space-y-3">
            {data.items.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/properties/${p.id}`}
                  className="block rounded-[24px] border border-line p-5 transition hover:border-cobalt"
                >
                  <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-soft">
                    <span className="font-semibold text-ink">{p.caseLabel}</span>
                    <span>{TYPE_LABEL[p.propertyType] ?? p.propertyType}</span>
                    {p.currentRound ? (
                      <span>{STATUS_LABEL[p.currentRound.status]}</span>
                    ) : null}
                    {p.currentRound && p.currentRound.failedCount > 0 ? (
                      <span>유찰 {p.currentRound.failedCount}회</span>
                    ) : null}
                  </div>
                  <p className="mt-1.5 text-[15px] font-semibold">
                    {p.buildingName ?? p.addressNormalized}
                  </p>
                  <p className="text-[13px] text-ink-soft">{p.addressNormalized}</p>
                  {p.currentRound ? (
                    <p className="mt-2 text-[14px]">
                      최저{" "}
                      <strong className="font-bold">
                        {formatWonKo(toWon(p.currentRound.minimumBidAmount))}
                      </strong>
                      <span className="text-ink-soft">
                        {" · 감정 "}
                        {formatWonKo(toWon(p.currentRound.appraisalAmount))}
                      </span>
                    </p>
                  ) : null}
                  {p.riskSummary.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.riskSummary.map((r) => (
                        <span
                          key={r}
                          className="rounded-full bg-stop-soft px-2.5 py-1 text-[11px] font-semibold text-stop"
                        >
                          {RISK_LABEL_KO[r]}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
